import React, { useContext, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import SvgMoney from '@components/ringil3/icons/Money.tsx';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue.tsx';
import { CurrencyLockTypeEnum, FeaturesEnum, PriceDTO } from '@api/ringil-api.v2.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect';
import PriceTag from '@components/RingilComponents/tags/priceTag/PriceTag.tsx';

interface ComponentProps {
  priceDto?: PriceDTO;
  expeditionId: string;
  onReload: () => void;
  showPrices?: boolean;
  readOnly: boolean;
  dataTestId?: string;
}

interface Form {
  price: number;
  currency: string;
  currencyLockType: CurrencyLockTypeEnum;
}

const RequestedPriceTag: React.FC<ComponentProps> = ({
                                                      priceDto,
                                                      expeditionId,
                                                      onReload,
                                                      readOnly,
                                                      showPrices = true,
                                                      dataTestId,
                                                    }) => {
  const { tr } = useTranslationLgs();
  const { changeExpeditionDistributionPrice } = useContext(ApiContext);
  const [open, setOpen] = useState(false);
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const form = useForm<Form>(
    yup.object().shape({
      price: yup.number().required('Price is required'),
      currency: yup.string().required(),
    }),
    { price: priceDto?.price, currency: priceDto?.currency, currencyLockType: CurrencyLockTypeEnum.NONE },
    d => save(d),
    false,
    false,
    null,
    'none',
  );

  const currencyLockEnabled = loggedUser?.features?.some(t => t === FeaturesEnum.CURRENCY_LOCK);

  const pane = (
    <>
      <VerticalFieldAndValue label={tr(`RequestedPriceTag.price`, `Price`)} required errorMessage={form.errors.price}>
        <InputPriceWithCurrency
          value={form.values.price}
          currency={form.values.currency}
          changePrice={p => {
            form.setFieldValue(form.names.price, p);
          }}
          changeCurrency={c => {
            form.setFieldValue(form.names.currency, c);
          }}
        />
      </VerticalFieldAndValue>
      {currencyLockEnabled && (
        <VerticalFieldAndValue
          label={tr('RequestedPriceTag.allowedCurrenciesLabel', 'Allowed currencies')}
          required={true}
          helpText={tr('RequestedPriceTag.allowedCurrenciesHelp', 'Allowed currencies help')}
        >
          <InputSelect
            model={[
              {
                label: tr('RequestedPriceTag.currencyLockTypeEnumNone', 'Without limitations'),
                code: CurrencyLockTypeEnum.NONE,
              },
              {
                label: tr('RequestedPriceTag.currencyLockTypeEnumOnlySelected', 'Only selected'),
                code: CurrencyLockTypeEnum.ONLY_SELECTED,
              },
            ]}
            selectedItemCode={form.values.currencyLockType || CurrencyLockTypeEnum.NONE}
            onSelect={(v) => form.setFieldValue(form.names.currencyLockType, v)}
          />
        </VerticalFieldAndValue>
      )}
    </>
  );

  const save = (data: Form) => {
    changeExpeditionDistributionPrice(
      expeditionId,
      {
        price: data.price,
        currency: data.currency,
        currencyLockType: data.currencyLockType,
      },
      () => {
        setOpen(false);
        onReload();
      },
    );
  };

  return (
    <>
      {isNotNullOrUndefined(priceDto) && (
        <ButtonTag
          label={tr(`RequestedPriceTag.title`, `Requested`)}
          value={showPrices ? <PriceTag priceDto={priceDto} badgeType={'none'} /> : '-'}
          icon={<SvgMoney />}
          actionIcon={<SvgTagEdit />}
          dataTestId={dataTestId}
          readonly={readOnly}
          onSave={(onSuccess) => {
            form.validateAndSend();
            onSuccess();
          }}
          autoClose
        >
          {pane}
        </ButtonTag>
      )}
    </>
  );
};

export default RequestedPriceTag;
